/**
 * 완료 조건 에디터
 */
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import type { CompletionRules, CompletionRulesOverrides } from '@/types/configuration'

interface CompletionRulesEditorProps {
  rules: CompletionRules
  onUpdate: (rules: CompletionRules) => void
  parentOverrides?: CompletionRulesOverrides
}

export const CompletionRulesEditor: React.FC<CompletionRulesEditorProps> = ({
  rules,
  onUpdate,
  parentOverrides
}) => {
  const { t } = useTranslation('configuration')
  // 기본값 처리
  const lessonSequenceMode = rules.lessonSequenceMode ?? 'sequential'

  const isFieldLocked = (field: keyof CompletionRulesOverrides) => parentOverrides?.[field] === false

  return (
    <div className="space-y-6">
      {/* 레슨 간 순서 설정 */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">{t('completionRules.lessonOrder')}</h4>
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-lg p-4 space-y-4">
          <p className="text-xs text-purple-700">
            {t('completionRules.lessonOrderDesc')}
          </p>
          
          {/* 순차/자유 선택 */}
          <div className="space-y-2">
            {isFieldLocked('lessonSequenceMode') && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <Lock className="w-3.5 h-3.5 text-gray-400" />
                {t('completionRules.lockedByParent')}
              </div>
            )}
            <label className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${isFieldLocked('lessonSequenceMode') ? 'opacity-60' : 'cursor-pointer hover:bg-white/50'}`}>
              <input
                type="radio"
                name="lessonSequence"
                checked={lessonSequenceMode === 'sequential'}
                onChange={() => onUpdate({ ...rules, lessonSequenceMode: 'sequential' })}
                disabled={isFieldLocked('lessonSequenceMode')}
                className="w-4 h-4 mt-0.5 text-purple-600 border-gray-300 focus:ring-purple-500 disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">{t('completionRules.sequential')}</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('completionRules.sequentialDesc')}
                </p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${isFieldLocked('lessonSequenceMode') ? 'opacity-60' : 'cursor-pointer hover:bg-white/50'}`}>
              <input
                type="radio"
                name="lessonSequence"
                checked={lessonSequenceMode === 'flexible'}
                onChange={() => onUpdate({ ...rules, lessonSequenceMode: 'flexible' })}
                disabled={isFieldLocked('lessonSequenceMode')}
                className="w-4 h-4 mt-0.5 text-purple-600 border-gray-300 focus:ring-purple-500 disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">{t('completionRules.flexible')}</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('completionRules.flexibleDesc')}
                </p>
              </div>
            </label>
          </div>

        </div>
      </div>

      {/* 레슨 내 완료 조건 */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">{t('completionRules.lessonCompletion')}</h4>
        <div className="space-y-4">
          {/* 모든 필수 컴포넌트 완료 */}
          <label className={`flex items-start gap-3 ${isFieldLocked('allRequiredMustComplete') ? 'opacity-60' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={rules.allRequiredMustComplete}
              onChange={(e) => onUpdate({ ...rules, allRequiredMustComplete: e.target.checked })}
              disabled={isFieldLocked('allRequiredMustComplete')}
              className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                {t('completionRules.allRequiredMustComplete')}
                {isFieldLocked('allRequiredMustComplete') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('completionRules.allRequiredMustCompleteDesc')}
              </p>
            </div>
          </label>

          {/* 순차적 진행 강제 (컴포넌트 간) */}
          <label className={`flex items-start gap-3 ${isFieldLocked('enforceSequentialProgress') ? 'opacity-60' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={rules.enforceSequentialProgress}
              onChange={(e) => onUpdate({ ...rules, enforceSequentialProgress: e.target.checked })}
              disabled={isFieldLocked('enforceSequentialProgress')}
              className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                {t('completionRules.enforceSequentialProgress')}
                {isFieldLocked('enforceSequentialProgress') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('completionRules.enforceSequentialProgressDesc')}
              </p>
            </div>
          </label>

          {/* 최소 점수 */}
          <div className={isFieldLocked('minimumScore') ? 'opacity-60' : ''}>
            <label className="block text-sm font-medium text-gray-900 mb-1 flex items-center gap-1.5">
              {t('completionRules.minimumPassingScore')}
              {isFieldLocked('minimumScore') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={0}
                max={100}
                value={rules.minimumScore ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value)
                  onUpdate({ ...rules, minimumScore: value })
                }}
                disabled={isFieldLocked('minimumScore')}
                placeholder={t('completionRules.minimumScorePlaceholder')}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
              />
              <span className="text-xs text-gray-500">
                {t('completionRules.minimumScoreHelp')}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {t('completionRules.minimumScoreEmpty')}
            </p>
          </div>
        </div>
      </div>

      {/* 설명 박스 */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-800 mb-2">{t('completionRules.infoTitle')}</h5>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• <strong>{t('completionRules.infoLessonOrder')}</strong>: {t('completionRules.infoLessonOrderDesc')}</li>
          <li>• <strong>{t('completionRules.infoComponentSequential')}</strong>: {t('completionRules.infoComponentSequentialDesc')}</li>
          <li>• {t('completionRules.infoMinimumScore')}</li>
        </ul>
      </div>
    </div>
  )
}
